import React from 'react';
import { CityStats, StatDeltas } from '../../types';
import { playUiSfx } from '../../utils/uiAudio';
import { surfaces, type as typeTokens, buttons, infoCards, cn } from './kit';

interface StatsBarProps {
    stats: CityStats;
    deltas: StatDeltas;
    showAiOffline?: boolean;
    onOpenDashboard: () => void;
    onOpenSOC: () => void;
    onOpenFinance: () => void;
    gameSpeed: number;
    setGameSpeed: (speed: number) => void;
    onOpenHR?: () => void;
    onOpenTPRM?: () => void;
    onOpenAudit?: () => void;
    onOpenEnhancedObjectives?: () => void;
    showOrgShortcuts?: boolean;
    compact?: boolean;
}

const StatsBar: React.FC<StatsBarProps> = ({
    stats,
    deltas,
    showAiOffline = false,
    onOpenDashboard,
    onOpenSOC,
    onOpenFinance,
    gameSpeed,
    setGameSpeed,
    onOpenHR,
    onOpenTPRM,
    onOpenAudit,
    onOpenEnhancedObjectives,
    showOrgShortcuts = true,
    compact = false,
}) => {
    
    const handleSpeedChange = (e: React.MouseEvent, speed: number) => {
        e.stopPropagation();
        playUiSfx('click');
        setGameSpeed(speed);
    };

    const eyebrow = cn(typeTokens.eyebrow, 'text-slate-400 text-[8px] md:text-[10px]');
    const statValue = cn(typeTokens.mono, 'font-bold drop-shadow-md', compact ? 'text-base md:text-lg' : 'text-base md:text-xl');
    const speedBtn = (active: boolean, activeClass: string) =>
        cn(
            'p-1 rounded text-[10px] w-6 h-6 flex items-center justify-center font-bold transition-all',
            active ? activeClass : 'bg-slate-800 text-slate-400 hover:bg-slate-700',
        );

    return (
        <div
            className={cn(
                surfaces.hud,
                'text-white flex flex-wrap items-center justify-start w-full max-w-full relative overflow-hidden group',
                compact ? 'p-1.5 gap-1.5' : 'p-2 md:p-3 gap-2 md:gap-4',
            )}
        >
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent opacity-50"></div>
            
            <div className="flex flex-col cursor-pointer hover:bg-white/5 rounded px-2 transition-colors" onClick={onOpenDashboard}>
            <span className={cn(eyebrow, 'flex items-center gap-1')}>Budget {deltas.budget !== 0 && <span className={`text-[8px] ${deltas.budget > 0 ? 'text-green-400' : 'text-red-400'}`}>{deltas.budget > 0 ? '▲' : '▼'}</span>}</span>
            <span data-testid="stats-budget" className={cn(statValue, 'font-black text-green-400', !compact && 'md:text-2xl')}>${stats.budget.toLocaleString()}</span>
            </div>
            
            {stats.loanCount > 0 && (
                <>
                    <div className="w-px h-6 md:h-8 bg-slate-700"></div>
                    <div
                      className={`flex cursor-pointer bg-red-900/40 hover:bg-red-900/60 border border-red-500/30 rounded transition-colors group/debt ${compact ? 'items-center gap-1 px-1.5 py-0.5' : 'flex-col px-2 py-0.5'}`}
                      onClick={onOpenFinance}
                      title="Open Finance dashboard"
                    >
                        <span className={`text-red-300 uppercase font-bold tracking-widest flex items-center gap-1 ${compact ? 'text-[8px]' : 'text-[8px] md:text-[10px] animate-pulse'}`}>
                          {compact ? `⚠ ${stats.loanCount}` : `Loans (${stats.loanCount}) ⚠️`}
                        </span>
                        {!compact && (
                          <span className={cn(typeTokens.mono, 'text-base md:text-xl font-bold text-white drop-shadow-md')}>-${(stats.loanCount * 25).toLocaleString()}/t</span>
                        )}
                    </div>
                </>
            )}

            <div className="w-px h-6 md:h-8 bg-slate-700"></div>
            <div className="flex flex-col cursor-pointer hover:bg-white/5 rounded px-2 transition-colors" onClick={onOpenDashboard}>
            <span className={cn(eyebrow, 'flex items-center gap-1')}>Compliance {deltas.compliance !== 0 && <span className={`text-[8px] ${deltas.compliance > 0 ? 'text-green-400' : 'text-red-400'}`}>{deltas.compliance > 0 ? '▲' : '▼'}</span>}</span>
            <span className={cn(statValue, 'text-cyan-300')}>{Math.floor(stats.compliance)}%</span>
            </div>
            <div className="w-px h-6 md:h-8 bg-slate-700"></div>
            <div className="flex flex-col cursor-pointer hover:bg-white/5 rounded px-2 transition-colors" onClick={onOpenSOC}>
            <span className={cn(eyebrow, 'flex items-center gap-1')}>Threat {deltas.threat !== 0 && <span className={`text-[8px] ${deltas.threat > 0 ? 'text-red-400' : 'text-emerald-400'}`}>{deltas.threat > 0 ? '▲' : '▼'}</span>}</span>
            <span className={cn(statValue, stats.threat > 50 ? 'text-red-500 animate-pulse' : 'text-emerald-400')}>{Math.floor(stats.threat)}%</span>
            </div>
            <div className="w-px h-6 md:h-8 bg-slate-700"></div>

            {/* Organizational Management */}
            {showOrgShortcuts && (onOpenHR || onOpenTPRM || onOpenAudit) && (
                <>
                    <div className="flex gap-1 md:gap-2">
                        {onOpenHR && (
                            <button
                                onClick={(e) => { e.stopPropagation(); onOpenHR(); }}
                                className={cn(buttons.primarySm, 'px-2 py-1 md:px-3 bg-blue-600/80 hover:bg-blue-500 border border-blue-500/30 shadow-lg hover:shadow-blue-500/25')}
                                title="Human Resources Management"
                            >
                                👥 HR
                            </button>
                        )}
                        {onOpenTPRM && (
                            <button
                                onClick={(e) => { e.stopPropagation(); onOpenTPRM(); }}
                                className={cn(buttons.primarySm, 'px-2 py-1 md:px-3 bg-orange-600/80 hover:bg-orange-500 border border-orange-500/30 shadow-lg hover:shadow-orange-500/25')}
                                title="Third-Party Risk Management"
                            >
                                🏢 TPRM
                            </button>
                        )}
                        {onOpenAudit && (
                            <button
                                onClick={(e) => { e.stopPropagation(); onOpenAudit(); }}
                                className={cn(buttons.primarySm, 'px-2 py-1 md:px-3 bg-purple-600/80 hover:bg-purple-500 border border-purple-500/30 shadow-lg hover:shadow-purple-500/25')}
                                title="Audit & Compliance Management"
                            >
                                🔍 Audit
                            </button>
                        )}
                        {onOpenEnhancedObjectives && (
                            <button
                                onClick={(e) => { e.stopPropagation(); onOpenEnhancedObjectives(); }}
                                className={cn(buttons.primarySm, 'px-2 py-1 md:px-3 bg-green-600/80 hover:bg-green-500 border border-green-500/30 shadow-lg hover:shadow-green-500/25')}
                                title="Enhanced Objectives & Goals"
                            >
                                🎯 Goals
                            </button>
                        )}
                    </div>
                    <div className="w-px h-6 md:h-8 bg-slate-700"></div>
                </>
            )}

            {/* Time Controls */}
            <div className="flex flex-col items-end gap-1">
                <div className="flex gap-1">
                    <button 
                        type="button"
                        data-testid="game-pause"
                        onClick={(e) => handleSpeedChange(e, 0)} 
                        className={speedBtn(gameSpeed === 0, 'bg-red-500 text-white')}
                        title="Pause"
                    >
                        ⏸
                    </button>
                    <button 
                        onClick={(e) => handleSpeedChange(e, 1)} 
                        className={speedBtn(gameSpeed === 1, 'bg-cyan-600 text-white')}
                        title="Normal Speed"
                    >
                        ▶
                    </button>
                    <button 
                        onClick={(e) => handleSpeedChange(e, 2)} 
                        className={speedBtn(gameSpeed === 2, 'bg-cyan-600 text-white')}
                        title="Fast Forward"
                    >
                        ⏩
                    </button>
                    <button 
                        onClick={(e) => handleSpeedChange(e, 5)} 
                        className={speedBtn(gameSpeed === 5, 'bg-purple-600 text-white animate-pulse')}
                        title="Ultra Speed"
                    >
                        ⚡
                    </button>
                </div>
                <div className={cn(eyebrow, 'flex justify-between w-full text-[8px] md:text-[9px]')}>
                    <span>Cycle</span>
                    <span data-testid="stats-day" className={cn(typeTokens.mono, 'text-white ml-1')}>{stats.day}</span>
                </div>
            </div>

            {showAiOffline && (
                <div
                    data-testid="ai-offline-badge"
                    className={cn(infoCards.value, 'flex items-center gap-1 py-1 text-[9px] md:text-[10px] font-bold uppercase tracking-wide')}
                    title="Set VITE_GEMINI_API_KEY in .env.local for live AI coach"
                >
                    <span aria-hidden>⚠</span>
                    <span>AI Offline</span>
                </div>
            )}
        </div>
    );
};

export default StatsBar;